/**
 * API backends: BACKEND_1 (default) and BACKEND_2 (OAuth v2 / refresh, etc.).
 * Env: REACT_APP_BACKEND_1_* and REACT_APP_BACKEND_2_*.
 * Legacy REACT_APP_BASE_URL_TEMPEDGE* still read as fallbacks.
 */

export const BACKEND_IDS = {
  BACKEND_1: 'backend_1',
  BACKEND_2: 'backend_2'
};

/** @deprecated use BACKEND_IDS */
export const BACKEND_TYPES = {
  OLD: BACKEND_IDS.BACKEND_1,
  NEW: BACKEND_IDS.BACKEND_2
};

/** @deprecated use BACKEND_IDS */
export const BACKEND_VERSIONS = {
  V1: BACKEND_IDS.BACKEND_1,
  V2: BACKEND_IDS.BACKEND_2
};

const DEFAULT_BACKEND = BACKEND_IDS.BACKEND_1;

const pickEnv = (primary, ...legacyKeys) => {
  const keys = [primary, ...legacyKeys];
  for (const key of keys) {
    const v = process.env[key];
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return undefined;
};

const ENV_KEYS = {
  [BACKEND_IDS.BACKEND_1]: {
    baseUrl: ['REACT_APP_BACKEND_1_BASE_URL', 'REACT_APP_BASE_URL_TEMPEDGE'],
    user: ['REACT_APP_BACKEND_1_USER', 'REACT_APP_TEMPEDGE_USER'],
    password: ['REACT_APP_BACKEND_1_PASSWORD', 'REACT_APP_TEMPEDGE_PASSWORD']
  },
  [BACKEND_IDS.BACKEND_2]: {
    baseUrl: ['REACT_APP_BACKEND_2_BASE_URL', 'REACT_APP_BASE_URL_TEMPEDGE_2'],
    user: ['REACT_APP_BACKEND_2_USER', 'REACT_APP_TEMPEDGE_USER_2'],
    password: ['REACT_APP_BACKEND_2_PASSWORD', 'REACT_APP_TEMPEDGE_PASSWORD_2'],
    fallbackTo: BACKEND_IDS.BACKEND_1
  }
};

const loadBackendConfig = (id) => {
  const mapping = ENV_KEYS[id];
  if (!mapping) {
    throw new Error(`Unknown backend id: ${id}`);
  }

  let baseUrl = pickEnv(...mapping.baseUrl);
  let user = pickEnv(...mapping.user);
  let password = pickEnv(...mapping.password);

  if (mapping.fallbackTo && (!baseUrl || !user || !password)) {
    const fb = loadBackendConfig(mapping.fallbackTo);
    baseUrl = baseUrl || fb.baseUrl;
    user = user || fb.user;
    password = password || fb.password;
  }

  const missing = [];
  if (!baseUrl) missing.push(mapping.baseUrl[0]);
  if (!user) missing.push(mapping.user[0]);
  if (!password) missing.push(mapping.password[0]);

  if (missing.length > 0) {
    throw new Error(`Missing env for ${id}: ${missing.join(', ')}`);
  }

  return { baseUrl, user, password, enabled: true };
};

const initializeBackendConfigs = () => {
  const configs = {};
  const errors = [];

  for (const id of Object.values(BACKEND_IDS)) {
    try {
      configs[id] = loadBackendConfig(id);
    } catch (error) {
      errors.push(`${id}: ${error.message}`);
      configs[id] = { baseUrl: '', user: '', password: '', enabled: false };
    }
  }

  if (errors.length > 0) {
    console.warn('Backend configuration warnings:', errors);
  }

  return configs;
};

const BACKEND_CONFIG = initializeBackendConfigs();

export const getBackendBaseUrl = (backendId = DEFAULT_BACKEND) => {
  const config = BACKEND_CONFIG[backendId];
  if (!config?.enabled) {
    throw new Error(`Backend ${backendId} is not configured`);
  }
  // Normalize root "/" (and any trailing slashes) to "" so that concatenating
  // baseUrl + "/api/..." yields "/api/..." (relative, same-origin) instead of
  // "//api/..." which the browser treats as a protocol-relative URL (host = "api").
  const base = config.baseUrl;
  return base === '/' ? '' : base.replace(/\/+$/, '');
};

export const getBackendCredentials = (backendId = DEFAULT_BACKEND) => {
  const config = BACKEND_CONFIG[backendId];
  if (!config?.enabled) {
    throw new Error(`Backend ${backendId} is not configured`);
  }
  return { user: config.user, password: config.password };
};

const DEFAULT_API_TIMEOUT_MS = 30000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 300000;

export const getApiTimeoutMs = () =>
  parseInt(process.env.REACT_APP_API_TIMEOUT_MS, 10) || parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || DEFAULT_API_TIMEOUT_MS;

export const getUploadTimeoutMs = () => parseInt(process.env.REACT_APP_UPLOAD_TIMEOUT_MS, 10) || DEFAULT_UPLOAD_TIMEOUT_MS;

export const getBackendConfig = (backendId = DEFAULT_BACKEND) => ({
  baseURL: getBackendBaseUrl(backendId),
  timeout: getApiTimeoutMs(),
  headers: { 'Content-Type': 'application/json' }
});

export const isBackendEnabled = (backendId) => BACKEND_CONFIG[backendId]?.enabled === true;

const API_VERSION_REGEX = /\/api\/v(\d+)\//;

export const detectBackendFromUrl = (urlPath) => {
  if (!urlPath || typeof urlPath !== 'string') {
    return DEFAULT_BACKEND;
  }
  const match = urlPath.match(API_VERSION_REGEX);
  if (!match) {
    return DEFAULT_BACKEND;
  }
  const n = parseInt(match[1], 10);
  if (n >= 2 && isBackendEnabled(BACKEND_IDS.BACKEND_2)) {
    return BACKEND_IDS.BACKEND_2;
  }
  if (n === 1 && isBackendEnabled(BACKEND_IDS.BACKEND_1)) {
    return BACKEND_IDS.BACKEND_1;
  }
  if (isBackendEnabled(BACKEND_IDS.BACKEND_2)) {
    return BACKEND_IDS.BACKEND_2;
  }
  return DEFAULT_BACKEND;
};

export const getBackendTypeForUrl = (urlPath, defaultBackendId = DEFAULT_BACKEND) => {
  const detected = detectBackendFromUrl(urlPath);
  if (detected !== defaultBackendId || urlPath?.includes('/api/v')) {
    return detected;
  }
  return defaultBackendId;
};

let defaultBackendId = DEFAULT_BACKEND;

export const setDefaultBackend = (backendId) => {
  if (!Object.values(BACKEND_IDS).includes(backendId)) {
    throw new Error(`Invalid backend: ${backendId}`);
  }
  if (!isBackendEnabled(backendId)) {
    throw new Error(`Backend ${backendId} is disabled`);
  }
  defaultBackendId = backendId;
};

export const getDefaultBackend = () => defaultBackendId;

/** True if URL targets API v2+ (uses BACKEND_2 when enabled). */
export const isApiV2Path = (urlPath) => detectBackendFromUrl(urlPath) === BACKEND_IDS.BACKEND_2;

const apiConfig = {
  BACKEND_IDS,
  getBackendBaseUrl,
  getBackendCredentials,
  getBackendConfig,
  isBackendEnabled,
  setDefaultBackend,
  getDefaultBackend,
  detectBackendFromUrl,
  getBackendTypeForUrl,
  isApiV2Path
};

export default apiConfig;
